import { Utils } from "@viamap/viamap2-common";
import React from "react";

export enum MessageType {
   ApiInfo = "ApiInfo",
   MessageInfo = "MessageInfo"
}

export type BroadcastMessageFormat = {
   status: "info" | "warning" | "error" | "success";
   affectedItems: {
      domain: string;
      url: string;
      statusmessage: { [languageCode: string]: string };
      licenseFeature: string[] | string;
   }[]
}
export type BroadcastMessageInfoFormat = {
   header: string;
   message: string;
}
export type BroadcastMessageInfoFormatWithMetaData = {
   header: string;
   message: string;
   type: 'All' | 'Org' | 'User';
   sendTo: string;
}

// Dual-bucket types
export type ApiBroadcast = BroadcastMessageFormat;
export type CustomBroadcast = BroadcastMessageInfoFormatWithMetaData;

export interface BroadcastBucket<TMost, TFiltered> {
   mostRecent?: TMost;
   filtered?: TFiltered;
   latestAcknowledged: boolean;
}

export interface BroadcastReceiverState {
   stateVersion: 2,
   activeTransactions: { [guid: string]: any },
   api: BroadcastBucket<ApiBroadcast, ApiBroadcast>,
   custom: BroadcastBucket<CustomBroadcast, CustomBroadcast>,
}

// ---------------------------------------------------- INITIAL STATE --------------------------------------------------------------

export function initialBroadcastReceiverState(): BroadcastReceiverState {

   return {
      stateVersion: 2, //Increment, when requiredFields are added
      activeTransactions: {},
      api: {
         latestAcknowledged: true,
         filtered: {
            status: "success",
            affectedItems: []
         }
      },
      custom: {
         latestAcknowledged: true
      },
   };
}



// ---------------------------------------------------- ACTIONS --------------------------------------------------------------
export enum BroadcastReceiverActionType {
   TxStarted,
   TxCompleted,
   TxFailed,

   Initialize,
   ProcessNewApiBroadcast,
   ProcessNewCustomBroadcast,
   AcknowledgeApiBroadcast,
   AcknowledgeCustomBroadcast

}


export interface TxStarted {
   type: BroadcastReceiverActionType.TxStarted;
   payload: { guid: string, action: BroadcastReceiverActions };
}

export interface TxCompleted {
   type: BroadcastReceiverActionType.TxCompleted;
   payload: { guid: string, action: BroadcastReceiverActions };
}

export interface TxFailed {
   type: BroadcastReceiverActionType.TxFailed;
   payload: { guid: string, action: BroadcastReceiverActions, error: any };
}


export interface Initialize {
   type: BroadcastReceiverActionType.Initialize;
   payload: {
      projectsElasticIndex: string,
      userId: string
   };
   results?: {
      success: boolean,
   };
}

export interface ProcessNewApiBroadcast {
   type: BroadcastReceiverActionType.ProcessNewApiBroadcast;
   payload: {
      myAvailableFeatures: string[],
      message: BroadcastMessageFormat
   };
}

export interface ProcessNewCustomBroadcast {
   type: BroadcastReceiverActionType.ProcessNewCustomBroadcast;
   payload: {
      message: BroadcastMessageInfoFormatWithMetaData,
      userRef: string,
      customerRef: string,
   };
}

export interface AcknowledgeApiBroadcast {
   type: BroadcastReceiverActionType.AcknowledgeApiBroadcast;
   payload: {};
}

export interface AcknowledgeCustomBroadcast {
   type: BroadcastReceiverActionType.AcknowledgeCustomBroadcast;
   payload: {};
}

// -------------------- utility functions to create an Action object ---------------------------------

export const actionTxStarted = (guid: string, action: BroadcastReceiverActions): TxStarted => ({
   type: BroadcastReceiverActionType.TxStarted,
   payload: { guid, action }
});

export const actionTxCompleted = (guid: string, action: BroadcastReceiverActions): TxCompleted => ({
   type: BroadcastReceiverActionType.TxCompleted,
   payload: { guid, action }
});

export const actionTxFailed = (guid: string, action: BroadcastReceiverActions, error: any): TxFailed => ({
   type: BroadcastReceiverActionType.TxFailed,
   payload: { guid, action, error }
});

export const initialize = (projectsElasticIndex: string, userId: string): Initialize => ({
   type: BroadcastReceiverActionType.Initialize,
   payload: { projectsElasticIndex, userId }
});


export type BroadcastReceiverActionsTransActional =
   Initialize;
export type BroadcastReceiverActionsNonTransActional = TxStarted
   | TxCompleted
   | TxFailed
   | ProcessNewApiBroadcast
   | ProcessNewCustomBroadcast
   | AcknowledgeApiBroadcast
   | AcknowledgeCustomBroadcast;
export type BroadcastReceiverActions = BroadcastReceiverActionsTransActional | BroadcastReceiverActionsNonTransActional;


// ---------------------------------------------------- ACTIONS --------------------------------------------------------------



// ---------------------------------------------------- TRANSACTIONAL REDUCER --------------------------------------------------------------

export function transactionalBroadcastReceiverReducer(action: BroadcastReceiverActionsTransActional, dispatch: any) {

   switch (action.type) {
      case BroadcastReceiverActionType.Initialize: {
         break;
      }
      default:
         // proceed directly to non-transational reducer for other actions
         dispatch(action);
   }
}


// ---------------------------------------------------- REDUCER --------------------------------------------------------------
export function BroadcastReceiverReducer(state: BroadcastReceiverState, action: BroadcastReceiverActions): BroadcastReceiverState {

   switch (action.type) {

      case BroadcastReceiverActionType.TxStarted:
         return {
            ...state,
            activeTransactions: { ...state.activeTransactions, [action.payload.guid]: action }
         };

      case BroadcastReceiverActionType.TxFailed: {

         let tmp = { ...state.activeTransactions };
         delete tmp[action.payload.guid];
         let msg = Utils.formatString(/*Localization.getTextSafeMode(*/"Error. Transaction {txname} returned with {error}"/*)*/, {
            txname: /*Localization.getTextSafeMode(*/BroadcastReceiverActionType[action.payload.action.type]/*)*/,
            error: /*Localization.getTextSafeMode(*/action.payload.error/*)*/
         });
         // Logger.logError("viamapLiceningState", BNBOActionType[action.payload.action.type], msg);
         if ((action.payload.action as any).errorCallback) {
            (action.payload.action as any).errorCallback(action.payload.error);
         }
         return {
            ...state,
            custom: {
               ...state.custom,
               mostRecent: { header: msg, message: msg, type: 'User', sendTo: '' },
               filtered: { header: msg, message: msg, type: 'User', sendTo: '' },
               latestAcknowledged: false
            },
            activeTransactions: tmp
         };
      }

      case BroadcastReceiverActionType.ProcessNewCustomBroadcast: {
         let msg = action.payload.message;

         if (msg.type === 'All' || (msg.type === 'Org' && msg.sendTo === action.payload.customerRef) || (msg.type === 'User' && msg.sendTo === action.payload.userRef)) {
            return {
               ...state,
               custom: {
                  ...state.custom,
                  mostRecent: msg,
                  filtered: msg,
                  latestAcknowledged: false
               }
            };
         } else {
            return { ...state };
         }


      }

      case BroadcastReceiverActionType.ProcessNewApiBroadcast: {
         let msg = action.payload.message;
         let myAvailableFeatures = action.payload.myAvailableFeatures;

         const newFilteredItems = msg.affectedItems.filter((item) => {
            if (typeof item.licenseFeature === "string") {
               return myAvailableFeatures.includes(item.licenseFeature);
            } else if (Array.isArray(item.licenseFeature)) {
               return item.licenseFeature.some((feature) => myAvailableFeatures.includes(feature));
            }
         });

         const newFilteredMessage = {
            status: newFilteredItems.length > 0 ? msg.status : "success",
            affectedItems: newFilteredItems
         };



         const isSameMessage = state.api && state.api.filtered &&
            JSON.stringify(state.api.filtered) === JSON.stringify(newFilteredMessage);

         if (isSameMessage) {
            return { ...state };
         } else {
            return {
               ...state,
               api: {
                  mostRecent: msg,
                  filtered: newFilteredMessage,
                  latestAcknowledged: false
               }
            };
         }
      }

      case BroadcastReceiverActionType.AcknowledgeApiBroadcast: {
         return {
            ...state,
            api: {
               ...state.api,
               latestAcknowledged: true
            }
         };
      }

      case BroadcastReceiverActionType.AcknowledgeCustomBroadcast: {
         return {
            ...state,
            custom: {
               ...state.custom,
               latestAcknowledged: true
            }
         };
      }

      default:
         throw new Error("Unknown action " + (action as BroadcastReceiverActions).type);
   }
}

// ---------------------------------------------------- generic stuff --------------------------------------------------------------

export const BroadcastReceiverContext = React.createContext<{
   state: BroadcastReceiverState;
   dispatch: React.Dispatch<BroadcastReceiverActions>;
}>({
   state: initialBroadcastReceiverState(),
   dispatch: () => undefined,
});
BroadcastReceiverContext.displayName = "BroadcastReceiverContext";

export class GenericTransactionManager {

   static dispatchMiddleware<ActionTypes>(dispatch: any, transactionalReducer: (action: any, dispatch: any) => any) {

      return (action: ActionTypes) => {
         transactionalReducer(action, dispatch);
      };

   }
}

